import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OptimizedImageByName from '../common/OptimizedImageByName';

interface OurServicesSectionProps {
  serviceType?: 'flower' | 'fruit' | 'cat_grass';
}

export const OurServicesSection = ({ serviceType = 'flower' }: OurServicesSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Determine which translation namespace to use based on service type
  const namespace = serviceType === 'fruit' ? 'fruits' : serviceType === 'cat_grass' ? 'catGrass' : 'flowers';

  return (
    <div className="bg-white px-6 py-6">
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-gray-800">{t(`${namespace}.ourServices`)}</h3>
      </div>
      
      <div>
        {serviceType === 'flower' ? (
          <>
            {/* Flower Subscription Card */}
            <div className="bg-white rounded-2xl py-4">
              <div className="flex items-center">
                <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <OptimizedImageByName imageName="servicesFlower" alt="Flower Service" className="h-12 w-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">{t('flowers.flowerSubscription')}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{t('flowers.flowerSubscriptionDesc')}</p>
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-100 my-1"></div>
            
            {/* Seasonal Fruits Card */}
            <div 
              className="bg-white rounded-2xl py-4 cursor-pointer"
              onClick={() => navigate('/fruits')}
            >
              <div className="flex items-center">
                <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <OptimizedImageByName imageName="servicesFruit" alt="Fruit Service" className="h-12 w-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">{t('flowers.seasonalFruits')}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{t('fruits.fruitSubscriptionDesc')}</p>
                </div>
              </div>
            </div>
          </>
        ) : serviceType === 'fruit' ? (
          <>
            {/* Flower Subscription Card (for fruits page) */}
            <div 
              className="bg-white rounded-2xl py-4 cursor-pointer"
              onClick={() => navigate('/flowers')}
            >
              <div className="flex items-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                  <OptimizedImageByName imageName="servicesFlower" alt="Flower Service" className="h-12 w-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">{t('fruits.flowerSubscription')}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{t('fruits.flowerSubscriptionDesc')}</p>
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-100 my-1"></div>
            
            {/* Fruit Subscription Card */}
            <div className="bg-white rounded-2xl py-4">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                  <OptimizedImageByName imageName="servicesFruit" alt="Fruit Service" className="h-8 w-8 object-contain" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">{t('fruits.fruitSubscription')}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{t('fruits.fruitSubscriptionDesc')}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Cat Grass page - show other services */}
            {/* Flower Subscription Card */}
            <div 
              className="bg-white rounded-2xl py-4 cursor-pointer"
              onClick={() => navigate('/flowers')}
            >
              <div className="flex items-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                  <OptimizedImageByName imageName="servicesFlower" alt="Flower Service" className="h-12 w-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">{t('catGrass.flowerSubscription')}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{t('catGrass.flowerSubscriptionDesc', 'Beautiful fresh flowers delivered weekly to your door.')}</p>
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-100 my-1"></div>
            
            {/* Fruit Subscription Card */}
            <div 
              className="bg-white rounded-2xl py-4 cursor-pointer"
              onClick={() => navigate('/fruits')}
            >
              <div className="flex items-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                  <OptimizedImageByName imageName="servicesFruit" alt="Fruit Service" className="h-12 w-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">{t('catGrass.fruitSubscription')}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{t('catGrass.fruitSubscriptionDesc', 'Fresh seasonal fruits delivered weekly.')}</p>
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-100 my-1"></div>
            
            {/* Cat Grass Subscription Card (current service) */}
            <div className="bg-white rounded-2xl py-4">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                  <OptimizedImageByName imageName="servicesCatGrass" alt="Cat Grass Service" className="h-12 w-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">{t('catGrass.catGrassSubscription')}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{t('catGrass.catGrassSubscriptionDesc')}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      <div className="mt-4 text-center">
        <button 
          onClick={() => navigate('/upcoming-services')}
          className="text-md w-full bg-white text-black px-4 py-2 rounded-full border border-gray-200"
        >
          {t('common.seeAll')}
        </button>
      </div>
    </div>
  );
}; 